/* ==========================================================================
   cbp-scroll-accordion.js - opens accordion items as the visitor scrolls.
   Used by CBP How It Works (cbp-how, cbp-how-2) and PDP Image + Accordion,
   on any section rendered with [data-scroll-acc] ("Open items on scroll").
   Loaded once from snippets/cbp-head.liquid, after cbp.js.

   Two modes, chosen per section and re-chosen when the window is resized:

   PIN (the section, or failing that its panels + image, fits on screen;
   usually desktop)
     The section's content sticks under the store header while the visitor
     scrolls through it; each stretch of scrolling opens the next item, then
     the section lets go and the page carries on. The scroll room is reserved
     up front (a spacer, plus a fixed minimum height for the content), so
     nothing on the page moves while items open and close.

   LINE (the section is taller than the screen, usually mobile)
     The item crossing a thin line across the middle of the screen opens; the
     rest close. When an item above closes, the new item's header is held in
     place so the page doesn't jump.

   Both modes:
   - Use IntersectionObserver only (no scroll listeners).
   - Open and close through summary.click(), so cbp.js does the height
     animation (and skips it for reduced motion) and pdp.js swaps the image /
     step caption exactly as it does for a real click.
   - Keep only one item open per section, in both scroll directions.
   - Respect a real click (or a block selected in the theme editor): auto-open
     pauses until the visitor has scrolled about a third of a screen.
   - Keep aria-expanded on each summary in step with what is open.
   - Run one instance per section; re-initialise on shopify:section:load.
   ========================================================================== */
(function () {
  if (window.CBPScrollAcc) return;

  var LINE = '-49.5% 0px -49.5% 0px'; // LINE mode: middle of the viewport
  var PIN_MS = 340;                   // cbp.js animates for 260ms
  var CLICK_GUARD_MS = 700;           // ignore layout-driven changes right after a click
  var CLICK_SCROLL = 0.35;            // after a click, auto-open waits for this much
                                      // scrolling (share of the viewport height)
  var STEP = 0.4;                     // PIN mode: scroll per item (share of viewport height)
  var STEP_MIN = 220;                 // ... but never less than this (px)
  var FIT_GAP = 16;                   // PIN mode needs this much room to spare (px)

  var instances = [];

  // html.scroll-acc-pinned while any section is in its pinned stretch
  // (pdp.css tucks the sticky buy bar away meanwhile).
  function markPinned() {
    var on = instances.some(function (i) { return i.mode === 'pin' && i.current >= 0; });
    document.documentElement.classList.toggle('scroll-acc-pinned', on);
  }

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  function isOpen(item) { return item.open && !item.classList.contains('is-closing'); }

  function summaryOf(item) {
    for (var i = 0; i < item.children.length; i++) {
      if (item.children[i].tagName === 'SUMMARY') return item.children[i];
    }
    return null;
  }

  function docTop(el) { return el.getBoundingClientRect().top + window.pageYOffset; }

  // Bottom of the store's sticky/fixed header, so pinned content sits below it.
  function headerBottom() {
    var bottom = 0;
    each(document.querySelectorAll('.shopify-section-group-header-group, header'), function (el) {
      var pos = getComputedStyle(el).position;
      if (pos !== 'sticky' && pos !== 'fixed') return;
      var r = el.getBoundingClientRect();
      if (r.height && r.top <= 1) bottom = Math.max(bottom, r.bottom);
      else if (r.height && pos === 'sticky') bottom = Math.max(bottom, r.height);
    });
    return Math.round(bottom);
  }

  /* Height of bars fixed to the bottom of the screen (app bars), so pinned
     content isn't hidden behind them. The PDP sticky buy bar isn't counted:
     it is tucked away while a section is pinned (html.scroll-acc-pinned,
     see pdp.css). Shopify's own preview bar is ignored, since shoppers never
     see it. */
  function footerBars() {
    var vh = window.innerHeight;
    var h = 0;
    each(document.body.children, function (el) {
      if (el.id === 'PBarNextFrameWrapper') return;
      var cs = getComputedStyle(el);
      if (cs.position !== 'fixed' || cs.visibility === 'hidden' || cs.display === 'none') return;
      var r = el.getBoundingClientRect();
      if (r.height > 0 && r.height < vh / 3 && r.bottom >= vh - 2 && r.top > vh / 2) h = Math.max(h, r.height);
    });
    return Math.round(h);
  }

  /* Interrupting a cbp.js animation with another click misbehaves: a click on
     a closing panel closes it again, and the cancelled animation's clean-up
     runs a moment later and wipes the new animation's state, which can leave
     two panels open. So a panel that is mid-animation is never clicked; it is
     put straight into its end state instead (no animation, nothing queued). */
  function animating(item) { return item.classList.contains('is-animating'); }

  function snap(item, open) {
    each(item.querySelectorAll('.cbp-collapse'), function (c) {
      if (c.getAnimations) each(c.getAnimations(), function (a) { a.cancel(); });
      c.style.height = '';
      c.style.opacity = '';
    });
    item.classList.remove('is-animating', 'is-closing');
    item.open = open;
  }

  function ScrollAcc(root) {
    this.root = root;
    this.items = Array.prototype.slice.call(root.querySelectorAll('details.cbp-panel'))
      .filter(function (d) { return summaryOf(d); });
    this.shell = root.querySelector(':scope > .cbp-shell') || root.firstElementChild;
    // Panels + image, without the heading row: pinned on its own when the
    // whole section is too tall (e.g. a laptop with a short window).
    this.grid = this.shell && this.shell.querySelector(':scope > .pdp-ia__grid, :scope > .cbp-how__grid');
    this.held = null;        // PIN: the element that sticks (shell or grid)
    this.mode = null;        // 'pin' or 'line'
    this.lock = null;        // { item, at, y } after a real click
    this.internal = false;   // true while we click summaries ourselves
    this.pinRaf = 0;
    this.busyUntil = 0;      // LINE: ignore line crossings until this time (ms)
    this.recheck = 0;
    this.current = -1;       // PIN: index of the stretch the visitor is in
    this.io = null;
    this.added = [];         // PIN: elements we inserted
    if (!this.items.length || !this.shell || !('IntersectionObserver' in window)) return;
    this.bindItems();
    this.setup();
  }

  ScrollAcc.prototype.bindItems = function () {
    var self = this;
    each(this.items, function (item) {
      var s = summaryOf(item);
      s.setAttribute('aria-expanded', isOpen(item) ? 'true' : 'false');

      s.addEventListener('click', function (e) {
        // Ours, or another script's (pdp.js closes panels by clicking them).
        if (self.internal || !e.isTrusted) return;
        // A real click or keyboard toggle: respect it.
        self.setLock(item);
        var before = docTop(s);
        // cbp.js updates the state in its own listener; read it afterwards.
        setTimeout(function () {
          if (isOpen(item)) {
            self.closeOthers(item);
            // LINE: an item above collapsing would move this header; hold it.
            if (self.mode === 'line') {
              var idx = self.items.indexOf(item);
              var above = self.items.slice(0, idx).some(function (o) { return o.classList.contains('is-closing'); });
              if (above) self.pin(s, before);
            }
          }
          self.syncAria();
        }, 0);
      });

      item.addEventListener('toggle', function () { self.syncAria(); });
    });
  };

  /* ---------- Mode choice ------------------------------------------------ */

  // Tallest the content gets with any one item open (or none). Each state is
  // laid out and read in the same frame, then the original state is put back,
  // so nothing is ever painted.
  ScrollAcc.prototype.measure = function () {
    var shell = this.shell, grid = this.grid;
    // A running animation would distort the heights: finish it first.
    each(this.items, function (i) { if (animating(i)) snap(i, isOpen(i)); });
    var was = this.items.map(function (i) { return i.open; });
    var max = { shell: 0, grid: 0 };
    function read() {
      max.shell = Math.max(max.shell, shell.offsetHeight);
      if (grid) max.grid = Math.max(max.grid, grid.offsetHeight);
    }
    each(this.items, function (i) { i.open = false; });
    read();
    var items = this.items;
    each(items, function (item) {
      each(items, function (i) { i.open = (i === item); });
      read();
    });
    each(items, function (i, n) { i.open = was[n]; });
    max.shell = Math.ceil(max.shell);
    max.grid = Math.ceil(max.grid);
    return max;
  };

  ScrollAcc.prototype.setup = function () {
    this.teardown();
    var vh = window.innerHeight;
    var top = headerBottom();
    var bars = footerBars();
    var room = vh - top - bars - FIT_GAP;
    var max = this.measure();
    this.vw = window.innerWidth;
    this.vh = vh;
    if (this.items.length < 2) this.setupLine();
    else if (max.shell <= room) this.setupPin(this.shell, top, bars, max.shell, vh);
    else if (this.grid && max.grid <= room) this.setupPin(this.grid, top, bars, max.grid, vh);
    else this.setupLine();
  };

  ScrollAcc.prototype.teardown = function () {
    if (this.io) { this.io.disconnect(); this.io = null; }
    cancelAnimationFrame(this.pinRaf);
    clearTimeout(this.recheck);
    each(this.added, function (el) { if (el.parentNode) el.parentNode.removeChild(el); });
    this.added = [];
    if (this.held) {
      var s = this.held.style;
      s.position = s.top = s.minHeight = '';
      this.held = null;
    }
    if (this.rootPos !== undefined) { this.root.style.position = this.rootPos; this.rootPos = undefined; }
    this.root.removeAttribute('data-scroll-acc-mode');
    document.documentElement.style.overflowAnchor = '';
    this.mode = null;
    this.current = -1;
    markPinned();
  };

  /* ---------- PIN mode --------------------------------------------------- */

  // "held" is the element that sticks: the whole section content, or just
  // its grid. Its parent is what it sticks inside, so the spacer goes there.
  ScrollAcc.prototype.setupPin = function (held, header, bars, maxH, vh) {
    var self = this;
    var n = this.items.length;
    var step = Math.max(STEP_MIN, Math.round(vh * STEP));
    // Centre the content between the header and any bottom bars.
    var top = header + Math.max(0, Math.floor((vh - header - bars - maxH) / 2));

    this.mode = 'pin';
    this.root.setAttribute('data-scroll-acc-mode', 'pin');
    if (getComputedStyle(this.root).position === 'static') {
      this.rootPos = this.root.style.position;
      this.root.style.position = 'relative';
    }

    // Where the held element sits before it sticks. Read now: once it is
    // sticky, its on-screen position depends on how far the page is scrolled.
    var heldTop = Math.round(held.getBoundingClientRect().top - this.root.getBoundingClientRect().top);

    this.held = held;
    var s = held.style;
    s.position = 'sticky';
    s.top = top + 'px';
    s.minHeight = maxH + 'px';

    // Extra room to scroll through while the content is held.
    var spacer = document.createElement('div');
    spacer.setAttribute('aria-hidden', 'true');
    spacer.style.height = ((n - 1) * step) + 'px';
    held.parentNode.insertBefore(spacer, held.nextSibling);
    this.added.push(spacer);

    /* One invisible marker per item, stacked down the scroll room. Marker i
       crosses the line where the content is pinned while the visitor is in
       item i's stretch, starting half a step before the pin begins. */
    var track = document.createElement('div');
    track.setAttribute('aria-hidden', 'true');
    track.style.cssText = 'position:absolute;left:0;top:0;width:1px;height:100%;pointer-events:none;visibility:hidden;';
    var markers = [];
    for (var i = 0; i < n; i++) {
      var m = document.createElement('div');
      var start = heldTop + i * step - Math.round(step / 2);
      var h = step;
      m.style.cssText = 'position:absolute;left:0;width:1px;top:' + start + 'px;height:' + h + 'px;';
      track.appendChild(m);
      markers.push(m);
    }
    this.root.appendChild(track);
    this.added.push(track);

    this.io = new IntersectionObserver(function (entries) {
      each(entries, function (e) {
        var idx = markers.indexOf(e.target);
        if (e.isIntersecting) self.current = idx;
        else if (self.current === idx) self.current = -1;
      });
      markPinned();
      self.syncPin();
    }, {
      rootMargin: '-' + top + 'px 0px -' + Math.max(0, vh - top - 1) + 'px 0px',
      threshold: 0
    });
    each(markers, function (m) { self.io.observe(m); });
  };

  ScrollAcc.prototype.syncPin = function () {
    if (this.stale() || this.current < 0 || this.locked()) return;
    var item = this.items[this.current];
    if (isOpen(item)) return;
    this.openItem(item);
    this.closeOthers(item);
    this.syncAria();
  };

  /* ---------- LINE mode -------------------------------------------------- */

  ScrollAcc.prototype.setupLine = function () {
    var self = this;
    this.mode = 'line';
    this.root.setAttribute('data-scroll-acc-mode', 'line');
    this.io = new IntersectionObserver(function () { self.onLineCross(); }, {
      rootMargin: LINE,
      threshold: 0
    });
    each(this.items, function (item) { self.io.observe(item); });
  };

  /* Each crossing is only a cue to look again: the decision is made from
     what is on the line now, so a crossing that happened during a click
     hold or a busy spell is never lost. */
  ScrollAcc.prototype.onLineCross = function () {
    var self = this;
    if (this.stale()) return;
    // While panels are collapsing and the page is being held in place, the
    // line sweeps over items that only moved, not ones the visitor scrolled
    // to. Wait until things settle, then check what is on the line.
    if (Date.now() < this.busyUntil) {
      clearTimeout(this.recheck);
      this.recheck = setTimeout(function () { self.checkLine(); }, this.busyUntil - Date.now() + 20);
      return;
    }
    if (this.locked()) return;
    var target = this.onLine();
    if (target && !isOpen(target)) this.activate(target);
  };

  // The item whose box crosses the middle line, if any (none in a gap).
  ScrollAcc.prototype.onLine = function () {
    var mid = window.innerHeight / 2;
    for (var i = 0; i < this.items.length; i++) {
      var r = this.items[i].getBoundingClientRect();
      if (r.top <= mid && r.bottom >= mid) return this.items[i];
    }
    return null;
  };

  /* After a busy spell (a fast flick can cross several items at once): open
     the last item whose top has passed the middle line, or the first one if
     none has. Only while the section is near the line, so a flick that
     carries the whole section off screen leaves it as it was. */
  ScrollAcc.prototype.checkLine = function () {
    var mid = window.innerHeight / 2;
    var first = this.items[0].getBoundingClientRect();
    var last = this.items[this.items.length - 1].getBoundingClientRect();
    if (first.top > mid + window.innerHeight / 2 || last.bottom < mid - window.innerHeight / 2) return;
    var on = this.items[0];
    each(this.items, function (item) {
      if (item.getBoundingClientRect().top <= mid) on = item;
    });
    if (isOpen(on) || this.locked()) return;
    this.activate(on);
  };

  ScrollAcc.prototype.activate = function (item) {
    var self = this;
    var header = summaryOf(item);
    var before = docTop(header);
    this.busyUntil = Date.now() + PIN_MS;
    this.openItem(item);
    if (this.closeOthers(item)) this.pin(header, before);
    this.syncAria();
    // During a fast scroll another script's late toggle can reopen a panel;
    // one frame later, make sure this is still the only open one.
    requestAnimationFrame(function () {
      if (isOpen(item)) self.closeOthers(item);
      self.syncAria();
    });
  };

  /* While items above "header" collapse, scroll by exactly the distance the
     header moves in the document, so it stays put on screen. Only layout
     movement is cancelled: the visitor's own scrolling doesn't change the
     header's document position, so it is never fought. Native scroll
     anchoring is off for the duration so the two never compensate twice. */
  ScrollAcc.prototype.pin = function (header, ref) {
    var html = document.documentElement;
    var end = Date.now() + PIN_MS;
    var self = this;
    cancelAnimationFrame(this.pinRaf);
    html.style.overflowAnchor = 'none';
    function step() {
      var y = docTop(header);
      var d = y - ref;
      if (Math.abs(d) > 0.5) window.scrollBy(0, d);
      ref = y;
      if (Date.now() < end) self.pinRaf = requestAnimationFrame(step);
      else html.style.overflowAnchor = '';
    }
    step(); // reduced motion: the close already happened, correct it now
  };

  /* ---------- Shared ----------------------------------------------------- */

  /* A click holds the accordion as the visitor left it until they have really
     moved on: at least CLICK_GUARD_MS (the click's own layout change settles)
     and CLICK_SCROLL of a screen of scrolling. Small scrolls don't count, even
     on mobile where closed items are only ~65px apart. Once passed, the hold
     is gone for good, so scrolling back to the same spot doesn't restore it.
     Distance is read when the observer next fires, not on every scroll. */
  ScrollAcc.prototype.setLock = function (item) {
    var lock = this.lock = { item: item, at: Date.now(), y: window.pageYOffset };
    // LINE: the page may be held in place just after the click; measure from there.
    setTimeout(function () { lock.y = window.pageYOffset; }, PIN_MS + 20);
  };

  ScrollAcc.prototype.locked = function () {
    if (!this.lock) return false;
    if (Date.now() - this.lock.at < CLICK_GUARD_MS) return true;
    if (Math.abs(window.pageYOffset - this.lock.y) < window.innerHeight * CLICK_SCROLL) return true;
    this.lock = null;
    return false;
  };

  ScrollAcc.prototype.toggle = function (item) {
    this.internal = true;
    try { summaryOf(item).click(); } finally { this.internal = false; }
  };

  // Open with cbp.js's animation, or snap open if it is mid-animation.
  ScrollAcc.prototype.openItem = function (item) {
    if (animating(item)) snap(item, true);
    else if (!item.open) this.toggle(item);
  };

  ScrollAcc.prototype.closeOthers = function (keep) {
    var self = this;
    var closingAbove = false;
    var keepIdx = this.items.indexOf(keep);
    each(this.items, function (other, idx) {
      if (other === keep || !isOpen(other)) return;
      if (idx < keepIdx) closingAbove = true;
      if (animating(other)) snap(other, false);
      else self.toggle(other);
    });
    return closingAbove;
  };

  ScrollAcc.prototype.syncAria = function () {
    each(this.items, function (item) {
      summaryOf(item).setAttribute('aria-expanded', isOpen(item) ? 'true' : 'false');
    });
  };

  // Theme editor: selecting a block opens it and counts as a click.
  ScrollAcc.prototype.selectBlock = function (blockEl) {
    var item = blockEl.closest('details.cbp-panel');
    if (!item || this.items.indexOf(item) === -1) return;
    this.setLock(item);
    this.openItem(item);
    this.closeOthers(item);
    this.syncAria();
  };

  // Re-choose the mode when the window really changes size. Mobile browsers
  // resize the viewport as the address bar shows and hides; ignore that.
  ScrollAcc.prototype.sizeChanged = function () {
    return window.innerWidth !== this.vw || Math.abs(window.innerHeight - this.vh) >= 120;
  };

  ScrollAcc.prototype.resized = function () {
    if (this.mode && this.sizeChanged()) this.setup();
  };

  // Also checked whenever the observer fires, in case a resize (rotation,
  // a window snapped to half the screen) was missed or ran mid-transition.
  ScrollAcc.prototype.stale = function () {
    if (!this.sizeChanged()) return false;
    var self = this;
    setTimeout(function () { self.setup(); }, 0);
    return true;
  };

  ScrollAcc.prototype.destroy = function () {
    this.teardown();
  };

  function init(scope) {
    each((scope || document).querySelectorAll('[data-scroll-acc]'), function (root) {
      if (root.dataset.scrollAccReady === '1') return;
      root.dataset.scrollAccReady = '1';
      instances.push(new ScrollAcc(root));
    });
  }

  function destroyIn(scope) {
    instances = instances.filter(function (inst) {
      if (scope.contains(inst.root)) { inst.destroy(); return false; }
      return true;
    });
  }

  var resizeTimer = 0;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () { each(instances, function (inst) { inst.resized(); }); }, 200);
  });

  // Fonts and lazy images change heights after load; measure again once the
  // page has finished loading.
  window.addEventListener('load', function () {
    each(instances, function (inst) { if (inst.mode) inst.setup(); });
  });

  window.CBPScrollAcc = { init: init };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroyIn(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    each(instances, function (inst) {
      if (inst.root.contains(e.target)) inst.selectBlock(e.target);
    });
  });
})();
